import type { GeneratorCode, NeutralizationTheoremCode } from "@csk/core";
import type {
  SafetyKernel,
  WasmBridgeAdmissionOutcome,
  WasmBridgeAdmissionReceipt,
} from "@csk/wasm";

export interface WasmBridgeProofCarrier {
  readonly admission: WasmBridgeAdmissionOutcome;
  readonly receipt: WasmBridgeAdmissionReceipt | null;
  readonly theoremCode: NeutralizationTheoremCode;
  readonly seamId: number | null;
  readonly bridgeId: number;
  readonly witnessId: number | null;
  readonly witnessKind: string | null;
  readonly phases: WasmBridgeAdmissionReceipt["phases"] | null;
  readonly consumedResources: readonly string[];
  readonly remainingWitnessUses: number;
  readonly replayRejected: boolean;
  readonly witnessReplayRejected: boolean | null;
}

export function createWasmBridgeProofCarrier(
  adapter: SafetyKernel,
  theoremCode: NeutralizationTheoremCode,
  generatorCode: GeneratorCode,
): WasmBridgeProofCarrier {
  const scope = `wasm-seam:${theoremCode}`;
  const seam = adapter.seams.define(theoremCode);
  const witness = adapter.seams.mintWitness(theoremCode, scope, 1);
  const bridge = adapter.seams.propose(seam, witness, "demo-matrix", "ts-wasm-contract");
  const admission = adapter.seams.admitWithSquare(bridge, scope, generatorCode);
  const receipt = admission.status === "admitted" ? adapter.seams.receipt(bridge) : null;
  const remainingWitnessUses = adapter.seams.remainingWitnessUses(witness);
  const witnessReplayRejected =
    admission.status === "admitted"
      ? adapter.seams.admit(
          adapter.seams.propose(seam, witness, "demo-matrix-replay", "ts-wasm-contract"),
          scope,
        ).status === "rejected-invalid-lifecycle"
      : null;
  const replay = adapter.seams.admit(bridge, scope);
  return {
    admission,
    receipt,
    theoremCode,
    seamId: receipt?.seamId ?? null,
    bridgeId: bridge,
    witnessId: receipt?.witnessId ?? null,
    witnessKind: receipt?.witnessKind ?? null,
    phases: receipt?.phases ?? null,
    consumedResources: receipt?.consumedResources ?? [],
    remainingWitnessUses,
    replayRejected: replay.status === "rejected-invalid-lifecycle",
    witnessReplayRejected,
  };
}
